// @ts-check
import React, { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { List, ListItem, Tooltip } from '@patternfly/react-core';
import _ from 'lodash';

import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';

import { getProviderClientList } from 'utils/asset';
import { ConnectionState } from 'utils/types';
import { isoToRelative } from 'utils/time';
import Icons, { Size } from 'components/Common/Icons';

/**
 * @typedef {Object} ConnectionStatus
 * @property {string} providerId - The provider id
 * @property {string} providerClientId - The provider client id
 * @property {ConnectionState} connectionState - The connection state
 * @property {string} lastActive - The last active date
 * @property {string} lastConnectionStateUpdate - The last connection state update date
 * @property {string} displayName - The display name
*/

/**
 * @typedef {Object} ConnectionQueryResult
 * @property {string} id - The id
 * @property {string} displayName - The display name
 * @property {Object} providerData - The provider data
 */

/**
 * @typedef {Object} UseConnectionStatus
 * @property {ConnectionStatus[]} connectionStatusList - The connection status
 * @property {boolean} isLoadingConnectionStatus - The loading status
 * @callback refresh - The refresh function for one or more provider clients
 * @callback refreshAll - The refresh function for all provider clients
*/

/**
 *
 * @param {ConnectionStatus} connectionStatus
 * @returns {JSX.Element}
 */
export const connectionIcon = (connectionStatus) => {
  const info =
  (<List isPlain>
    <ListItem>{ connectionStatus.displayName } ({ connectionStatus.providerClientId })</ListItem>
    <ListItem>Last Active: { isoToRelative(connectionStatus.lastActive) }</ListItem>
    <ListItem>Last Connection Update: { isoToRelative(connectionStatus.lastConnectionStateUpdate) }</ListItem>
  </List>);
  switch (connectionStatus.connectionState) {
    case ConnectionState.Connected:
      return (<Tooltip content={info} position="top" entryDelay={1000} exitDelay={1000}>
        <Icons.Connected size={Size.LARGE} style={{ color: 'green' }}/>
      </Tooltip>);
    case ConnectionState.Disconnected:
      return (<Tooltip content={info} position="top" entryDelay={1000} exitDelay={1000}>
        <Icons.Disconnected size={Size.LARGE} style={{ color: 'red' }}/>
      </Tooltip>);
    case ConnectionState.Unknown:
      return (<Tooltip content={info} position="top" entryDelay={1000} exitDelay={1000}>
        <Icons.Unknown size={Size.MEDIUM} />
      </Tooltip>);
    default:
      return <></>;
  }
};

/**
 *
 * @param {ConnectionState} connectionState
 * @returns {ConnectionState}
 */
const parseConnectionState = (connectionState) => {
  switch (connectionState) {
    case 'Connected':
      return ConnectionState.Connected;
    case 'Disconnected':
      return ConnectionState.Disconnected;
    case 'Disabled':
      return 'disabled';
    default:
      return ConnectionState.Unknown;
  }
};

/**
 * return the list of connection status for each provider client found in the asset list passed in
 * @param {import('utils/types').AssetType[]} assets - List of assets
 * @returns {UseConnectionStatus} The connection status
 */
export const useConnectionStatus = (assets) => {

  const api = useAPI();
  const providerClients = getProviderClientList(assets);

  const queries = useMemo(() => {
    const clients = providerClients.map((providerClient) => {
      if (!providerClient?.providerId || !providerClient?.providerClientId) {
        return null;
      }
      return {
        queryKey: ['connectionStatus', providerClient.providerId, providerClient.providerClientId],
        queryFn: createQueryRequest(api.core.getProviderClient),
        enabled: !!providerClient.providerId && !!providerClient.providerClientId,
        onError: (error) => {
          console.error(error);
        },
        staleTime: 1000 * 60 * 5 // 5 minutes
      };
    });
    return _.compact(clients);
  }, [api.core, providerClients]);
  const connectionStatusQueries = useQueries({ queries: queries });
  const isLoadingConnectionStatus = connectionStatusQueries.some((query) => query.isLoading);

  // TODO: update to handle other providers than iothubdevice
  return useMemo(() => {
    const merged = _.map(queries, (query, index) => {
      return {
        ...query,
        ...connectionStatusQueries[index]
      };
    });
    return {
      connectionStatusList: merged.map((query) => {
        const data = /** @type {ConnectionQueryResult} */(query.data);
        return {
          providerId: query.queryKey?.[1],
          providerClientId: data?.id,
          connectionState: parseConnectionState(data?.providerData?.iotHubDevice?.connectionState),
          lastActive: data?.providerData?.iotHubDevice?.lastActivityTime,
          lastConnectionStateUpdate: data?.providerData?.iotHubDevice?.connectionStateUpdatedTime,
          displayName: data?.displayName
        };
      }),
      isLoadingConnectionStatus: isLoadingConnectionStatus,
      refresh: () => {},
      refreshAll: () => {}
    };
  }, [connectionStatusQueries, isLoadingConnectionStatus, queries]);
};

